import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import SocialShareButtons from "../../components/SocialShareButtons";
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  Loader2,
  Leaf,
  Share2,
  Mail,
  CheckCircle,
  ArrowRight,
  Tag,
  User,
  Clock,
  Sparkles,
  X,
  ZoomIn,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

const SECTOR_CONFIG = {
  estetica: { 
    gradient: "from-rose-500 to-pink-600",
    lightBg: "bg-rose-50",
    textColor: "text-rose-600",
    name: "Estetica Oncologica"
  },
  parrucchieri: { 
    gradient: "from-amber-500 to-orange-600",
    lightBg: "bg-amber-50",
    textColor: "text-amber-600",
    name: "Hair Care"
  },
  farmacie: { 
    gradient: "from-emerald-500 to-teal-600",
    lightBg: "bg-emerald-50",
    textColor: "text-emerald-600",
    name: "Farmacia"
  },
  retail: { 
    gradient: "from-violet-500 to-purple-600",
    lightBg: "bg-violet-50",
    textColor: "text-violet-600",
    name: "Retail"
  },
  centri_benessere: { 
    gradient: "from-sky-500 to-blue-600",
    lightBg: "bg-sky-50",
    textColor: "text-sky-600",
    name: "Wellness"
  },
  // Categorie Benessere
  meditazione: {
    gradient: "from-purple-500 to-indigo-600",
    lightBg: "bg-purple-50",
    textColor: "text-purple-600",
    name: "Meditazione"
  },
  respirazione: {
    gradient: "from-cyan-500 to-blue-600",
    lightBg: "bg-cyan-50",
    textColor: "text-cyan-600",
    name: "Respirazione"
  },
  yoga: {
    gradient: "from-teal-500 to-emerald-600",
    lightBg: "bg-teal-50",
    textColor: "text-teal-600",
    name: "Yoga"
  },
  stress: {
    gradient: "from-orange-500 to-red-500",
    lightBg: "bg-orange-50",
    textColor: "text-orange-600",
    name: "Gestione Stress"
  },
  alimentazione: {
    gradient: "from-green-500 to-lime-600",
    lightBg: "bg-green-50",
    textColor: "text-green-600",
    name: "Alimentazione"
  },
  benessere_generale: {
    gradient: "from-pink-500 to-rose-600",
    lightBg: "bg-pink-50",
    textColor: "text-pink-600",
    name: "Benessere Generale"
  },
  natura: {
    gradient: "from-emerald-500 to-green-600",
    lightBg: "bg-emerald-50",
    textColor: "text-emerald-600",
    name: "Natura e Benessere"
  },
  sonno: {
    gradient: "from-indigo-500 to-purple-600",
    lightBg: "bg-indigo-50",
    textColor: "text-indigo-600",
    name: "Sonno e Riposo"
  }
};

// Simple Markdown-like rendering with lightbox support
function renderContent(content, onImageClick) {
  if (!content) return null;
  
  const lines = content.split('\n');
  const elements = [];
  let currentList = [];
  let listType = null;
  
  const flushList = () => {
    if (currentList.length > 0) {
      if (listType === 'ul') {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-2 my-4 text-slate-700">
            {currentList.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        );
      } else {
        elements.push(
          <ol key={`list-${elements.length}`} className="list-decimal list-inside space-y-2 my-4 text-slate-700">
            {currentList.map((item, i) => <li key={i}>{item}</li>)}
          </ol>
        );
      }
      currentList = [];
      listType = null;
    }
  };
  
  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    // Images: ![alt](url)
    const imageMatch = trimmedLine.match(/^!\[(.*?)\]\((.*?)\)$/);
    if (imageMatch) {
      flushList();
      const altText = imageMatch[1];
      const imageUrl = imageMatch[2];
      elements.push(
        <div key={index} className="my-8 relative group cursor-pointer" onClick={() => onImageClick && onImageClick(imageUrl)}>
          <img 
            src={imageUrl} 
            alt={altText}
            className="w-full rounded-xl shadow-lg object-cover object-[center_20%] max-h-96 transition-transform duration-300 group-hover:scale-[1.02]"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-xl transition-colors duration-300 flex items-center justify-center">
            <ZoomIn className="w-10 h-10 text-white opacity-0 group-hover:opacity-80 transition-opacity duration-300 drop-shadow-lg" />
          </div>
        </div>
      );
      return;
    }
    
    // Image caption: *text* (standalone italics line after image)
    if (trimmedLine.startsWith('*') && trimmedLine.endsWith('*') && !trimmedLine.includes('**')) {
      const captionText = trimmedLine.slice(1, -1);
      // Check if previous element was an image
      if (elements.length > 0) {
        elements.push(
          <p key={index} className="text-center text-slate-500 italic text-sm mt-2 mb-6">
            {captionText}
          </p>
        );
        return;
      }
    }
    
    // Horizontal rule
    if (trimmedLine === '---' || trimmedLine === '***') {
      flushList();
      elements.push(
        <hr key={index} className="my-8 border-slate-200" />
      );
      return;
    }
    
    // Headers
    if (trimmedLine.startsWith('### ')) {
      flushList();
      elements.push(
        <h3 key={index} className="text-xl font-semibold text-slate-900 mt-8 mb-4">
          {trimmedLine.replace('### ', '')}
        </h3>
      );
    } else if (trimmedLine.startsWith('## ')) {
      flushList();
      elements.push(
        <h2 key={index} className="text-2xl font-bold text-slate-900 mt-10 mb-4 font-serif">
          {trimmedLine.replace('## ', '')}
        </h2>
      );
    } else if (trimmedLine.startsWith('# ')) {
      flushList();
      // Skip H1 as it's the title
    }
    // Bullet lists
    else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      currentList.push(trimmedLine.replace(/^[-*]\s/, ''));
    }
    // Numbered lists
    else if (/^\d+\.\s/.test(trimmedLine)) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      currentList.push(trimmedLine.replace(/^\d+\.\s/, ''));
    }
    // Bold text and paragraphs
    else if (trimmedLine) {
      flushList();
      let processedLine = trimmedLine
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
      
      elements.push(
        <p 
          key={index} 
          className="text-slate-700 leading-relaxed my-4"
          dangerouslySetInnerHTML={{ __html: processedLine }}
        />
      );
    }
  });
  
  flushList();
  return elements;
}

export default function ArticleDetailPage() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [scientificRefs, setScientificRefs] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Newsletter
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  
  // Lightbox per ingrandire immagini
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadArticle();
  }, [articleId]);

  // Carica riferimenti scientifici quando l'articolo è disponibile
  useEffect(() => {
    if (article && articleId) {
      loadScientificReferences();
    }
  }, [article, articleId]);

  // Aggiorna meta tags dinamicamente per SEO e social sharing
  useEffect(() => {
    if (article) {
      // Titolo pagina
      document.title = `${article.title} | La Scienza del Benessere`;
      
      // Meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = article.meta_description || article.seo?.meta_description || article.title;
      
      // Open Graph tags
      const ogTags = {
        'og:title': article.title,
        'og:description': article.meta_description || article.seo?.meta_description || '',
        'og:image': article.header_image || 'https://odr-academy.it/og-image.png',
        'og:url': window.location.href,
        'og:type': 'article',
        'og:site_name': 'La Scienza del Benessere - ODR Academy'
      };
      
      Object.entries(ogTags).forEach(([property, content]) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', property);
          document.head.appendChild(tag);
        }
        tag.content = content;
      });
      
      // Twitter Card tags
      const twitterTags = {
        'twitter:card': 'summary_large_image',
        'twitter:title': article.title,
        'twitter:description': article.meta_description || article.seo?.meta_description || '',
        'twitter:image': article.header_image || 'https://odr-academy.it/og-image.png'
      };
      
      Object.entries(twitterTags).forEach(([name, content]) => {
        let tag = document.querySelector(`meta[name="${name}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.name = name;
          document.head.appendChild(tag);
        }
        tag.content = content;
      });

      // Meta keywords + author
      const kw = Array.isArray(article.keywords) ? article.keywords.join(', ') : (article.keywords || article.seo?.keywords || '');
      if (kw) {
        let kwTag = document.querySelector('meta[name="keywords"]');
        if (!kwTag) { kwTag = document.createElement('meta'); kwTag.name = 'keywords'; document.head.appendChild(kwTag); }
        kwTag.content = Array.isArray(kw) ? kw.join(', ') : kw;
      }
      let authorTag = document.querySelector('meta[name="author"]');
      if (!authorTag) { authorTag = document.createElement('meta'); authorTag.name = 'author'; document.head.appendChild(authorTag); }
      authorTag.content = article.seo?.author || 'ODR Academy - La Scienza del Benessere';

      // Canonical URL
      const canonicalHref = article.seo?.canonical_url || `https://odr-academy.it/benessere/articolo/${article.article_id}`;
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) { canonicalLink = document.createElement('link'); canonicalLink.rel = 'canonical'; document.head.appendChild(canonicalLink); }
      canonicalLink.href = canonicalHref;
    }
    
    // Cleanup
    return () => {
      document.title = 'La Scienza del Benessere | ODR Academy';
    };
  }, [article]);

  const loadArticle = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/public/articles/${articleId}`);
      if (response.ok) {
        const data = await response.json();
        setArticle(data);
        // Carica articoli correlati dopo aver caricato l'articolo
        loadRelatedArticles(articleId);
      } else if (response.status === 404) {
        setError("Articolo non trovato");
      } else {
        setError("Errore nel caricamento dell'articolo");
      }
    } catch (err) {
      console.error("Errore:", err);
      setError("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedArticles = async (currentArticleId) => {
    try {
      const response = await fetch(`${API_URL}/api/public/articles/${currentArticleId}/related?limit=3`);
      if (response.ok) {
        const data = await response.json();
        setRelatedArticles(data.related || []);
      }
    } catch (err) {
      console.error("Errore caricamento articoli correlati:", err);
    }
  };

  const loadScientificReferences = async () => {
    setLoadingRefs(true);
    try {
      const response = await fetch(`${API_URL}/api/public/article/${articleId}/references?limit=3`);
      if (response.ok) {
        const data = await response.json();
        setScientificRefs(data.references || []);
      }
    } catch (err) {
      console.error("Errore caricamento riferimenti scientifici:", err);
    } finally {
      setLoadingRefs(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.meta_description || article.seo?.meta_description,
          url: url
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti!");
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      const response = await fetch(`${API_URL}/api/public/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        setSubscribed(true);
        toast.success("Iscrizione completata!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore durante l'iscrizione");
      }
    } catch (err) {
      toast.error("Errore di connessione");
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center p-4" data-testid="article-error">
        <BookOpen className="w-16 h-16 text-slate-300 mb-4" />
        <h1 className="text-2xl font-bold text-slate-800 mb-2">{error}</h1>
        <p className="text-slate-500 mb-6">L'articolo richiesto non è disponibile.</p>
        <Link to="/benessere/articoli">
          <Button className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna agli Articoli
          </Button>
        </Link>
      </div>
    );
  }

  const config = SECTOR_CONFIG[article.sector] || SECTOR_CONFIG.estetica;
  const readingTime = Math.ceil((article.word_count || 500) / 200);

  // JSON-LD Schema per SEO
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description || article.seo?.meta_description || "",
    "image": article.header_image || "https://odr-academy.it/og-image.png",
    "author": {
      "@type": "Organization",
      "name": "La Scienza del Benessere - ODR Academy"
    },
    "publisher": {
      "@type": "Organization",
      "name": "ODR Academy",
      "logo": {
        "@type": "ImageObject",
        "url": "https://odr-academy.it/og-image.png"
      }
    },
    "datePublished": article.created_at,
    "dateModified": article.updated_at || article.created_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://odr-academy.it/benessere/articolo/${articleId}`
    },
    "keywords": article.keywords?.join(", ") || article.categoria_display || "benessere"
  };

  // FAQ Schema se ci sono domande nell'articolo
  const faqSchema = article.faq && article.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="article-detail-page">
      {/* JSON-LD Schema per SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/benessere" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif text-lg font-semibold text-slate-800 hidden sm:inline">La Scienza del Benessere</span>
            </Link>
            
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleShare} className="rounded-full" data-testid="share-btn">
                <Share2 className="w-4 h-4" />
              </Button>
              <Link to="/login">
                <Button variant="outline" size="sm" className="rounded-full">
                  Area Riservata
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <Link to="/benessere/articoli" className="inline-flex items-center text-slate-500 hover:text-rose-600 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna agli articoli
          </Link>
        </nav>

        {/* Article Header */}
        <header className="mb-10">
          <Badge className={`${config.lightBg} ${config.textColor} mb-4`} data-testid="article-sector">
            {article.categoria_display || config.name}
          </Badge>
          
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6" data-testid="article-title">
            {article.title}
          </h1>
          
          {(article.meta_description || article.seo?.meta_description) && (
            <p className="text-xl text-slate-600 leading-relaxed mb-6">
              {article.meta_description || article.seo?.meta_description}
            </p>
          )}
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(article.created_at).toLocaleDateString("it-IT", {
                day: "numeric",
                month: "long",
                year: "numeric"
              })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {readingTime} min di lettura
            </span>
            <span className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              {article.word_count} parole
            </span>
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {article.is_personalized && article.user_name 
                ? `Per ${article.user_name}` 
                : "ODR Content Team"}
            </span>
          </div>
          
          {/* Social Share Buttons */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <SocialShareButtons 
              url={`https://odr-academy.it/benessere/articoli/${articleId}`}
              title={article.title}
              description={article.meta_description}
            />
          </div>
        </header>

        {/* Header Image (AI Generated or Default) - Click to zoom */}
        {article.header_image && (
          <div 
            className="mb-10 rounded-2xl overflow-hidden shadow-lg cursor-pointer group relative" 
            data-testid="article-header-image"
            onClick={() => setLightboxImage(article.header_image)}
          >
            <img 
              src={article.header_image} 
              alt={article.title}
              className="w-full h-64 sm:h-80 lg:h-96 object-cover object-[center_20%] transition-transform duration-300 group-hover:scale-[1.02]"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
              <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-80 transition-opacity duration-300 drop-shadow-lg" />
            </div>
            {article.ai_generated_image && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-2 text-center">
                <span className="text-white text-xs font-medium flex items-center justify-center gap-1">
                  <span className="animate-pulse">✨</span>
                  Immagine generata con AI
                </span>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="prose prose-lg max-w-none" data-testid="article-content">
          {renderContent(article.content, setLightboxImage)}
        </div>

        {/* Keywords/Tags */}
        {article.keywords && article.keywords.length > 0 && (
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-slate-400" />
              {article.keywords.map((keyword, i) => (
                <Badge key={i} variant="outline" className="rounded-full">
                  {keyword}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* FAQ Section if available */}
        {article.faq_schema && article.faq_schema.length > 0 && (
          <div className="mt-12 pt-8 border-t border-slate-200">
            <h2 className="font-serif text-2xl font-bold text-slate-900 mb-6">Domande Frequenti</h2>
            <div className="space-y-4">
              {article.faq_schema.map((faq, i) => (
                <Card key={i} className="border-0 bg-slate-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-slate-800">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Scientific Sources */}
        {article.scientific_sources && article.scientific_sources.length > 0 && (
          <div className="mt-12 pt-8 border-t border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-4">Fonti Scientifiche</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              {article.scientific_sources.map((source, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-rose-500">•</span>
                  <span>{source.title} ({source.source_type}, {source.year})</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </article>

      {/* Scientific References Section - Fonti Scientifiche da Semantic Scholar */}
      {scientificRefs.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="scientific-refs-section">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-bold text-slate-900">
                  Fonti Scientifiche
                </h3>
                <p className="text-sm text-slate-500">Paper peer-reviewed da Semantic Scholar</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {scientificRefs.map((paper, i) => (
                <div 
                  key={i} 
                  className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                  data-testid={`scientific-ref-${i}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <a 
                        href={paper.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-medium text-slate-800 hover:text-blue-600 transition-colors line-clamp-2"
                      >
                        {paper.title}
                      </a>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {paper.authors?.join(", ") || "N/A"}
                        </span>
                        {paper.year && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {paper.year}
                          </span>
                        )}
                        {paper.citations > 0 && (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-xs">
                            {paper.citations} citazioni
                          </Badge>
                        )}
                      </div>
                      {paper.venue && paper.venue !== "N/A" && (
                        <p className="mt-1 text-xs text-slate-400 italic">{paper.venue}</p>
                      )}
                    </div>
                    {paper.open_access_pdf && (
                      <a 
                        href={paper.open_access_pdf} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="shrink-0 px-3 py-1.5 bg-green-100 text-green-700 text-xs font-medium rounded-lg hover:bg-green-200 transition-colors"
                      >
                        PDF Gratuito
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <p className="mt-4 text-xs text-slate-400 text-center">
              Riferimenti recuperati automaticamente da Semantic Scholar (214M+ paper scientifici)
            </p>
          </div>
        </section>
      )}

      {/* Related Articles Section - Potrebbe interessarti anche */}
      {relatedArticles.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="related-articles-section">
          <div className="border-t border-slate-200 pt-10">
            <div className="flex items-center gap-3 mb-8">
              <Sparkles className="w-6 h-6 text-teal-600" />
              <h2 className="font-serif text-2xl font-bold text-slate-900">
                Potrebbe interessarti anche
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((relatedArticle) => {
                const relatedConfig = SECTOR_CONFIG[relatedArticle.sector] || SECTOR_CONFIG[relatedArticle.categoria_display?.toLowerCase()] || SECTOR_CONFIG.estetica;
                return (
                  <Link 
                    key={relatedArticle.article_id} 
                    to={`/benessere/articolo/${relatedArticle.article_id}`}
                    onClick={() => window.scrollTo(0, 0)}
                    data-testid={`related-article-${relatedArticle.article_id}`}
                  >
                    <Card className="group hover:shadow-lg transition-all duration-300 border-0 bg-white overflow-hidden h-full">
                      {/* Header Image */}
                      {relatedArticle.header_image && (
                        <div className="h-36 overflow-hidden bg-slate-100">
                          <img 
                            src={relatedArticle.header_image} 
                            alt={relatedArticle.title}
                            className="w-full h-full object-cover object-[center_20%] group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      
                      <CardContent className="pt-4">
                        <Badge className={`${relatedConfig.lightBg} ${relatedConfig.textColor} mb-3 text-xs`}>
                          {relatedArticle.categoria_display || relatedConfig.name}
                        </Badge>
                        <h3 className="font-semibold text-slate-800 group-hover:text-teal-600 transition-colors line-clamp-2 text-sm leading-snug">
                          {relatedArticle.title}
                        </h3>
                        {relatedArticle.meta_description && (
                          <p className="text-slate-500 text-xs mt-2 line-clamp-2">
                            {relatedArticle.meta_description}
                          </p>
                        )}
                        <div className="flex items-center mt-3 text-xs text-teal-600 font-medium">
                          Leggi di più
                          <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="bg-gradient-to-br from-rose-600 to-pink-700 py-16 mt-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Mail className="w-12 h-12 text-white/80 mx-auto mb-4" />
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-4">
            Ti è piaciuto questo articolo?
          </h2>
          <p className="text-rose-100 mb-6">
            Iscriviti alla newsletter per ricevere nuovi contenuti direttamente nella tua inbox.
          </p>
          
          {subscribed ? (
            <div className="bg-white/20 rounded-xl p-6" data-testid="article-newsletter-success">
              <CheckCircle className="w-10 h-10 text-white mx-auto mb-2" />
              <p className="text-white font-semibold">Grazie per l'iscrizione!</p>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="article-newsletter-form">
              <Input
                type="email"
                placeholder="La tua email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white/10 border-white/20 text-white placeholder:text-white/60 rounded-xl h-12 flex-1"
              />
              <Button 
                type="submit" 
                disabled={subscribing}
                className="bg-white text-rose-600 hover:bg-rose-50 rounded-xl h-12 px-6"
              >
                {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Iscriviti"}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} ODR - Oncocosmetics Derma Ritual. Tutti i diritti riservati.
          </p>
          <p className="text-slate-500 text-xs mt-2">
            Contenuto generato con AI e revisionato dal team ODR.
          </p>
        </div>
      </footer>

      {/* Lightbox per ingrandire le immagini */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImage(null)}
          data-testid="image-lightbox"
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
            onClick={() => setLightboxImage(null)}
            aria-label="Chiudi"
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={lightboxImage} 
            alt="Immagine ingrandita"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-4 text-white/70 text-sm">
            Clicca ovunque per chiudere
          </p>
        </div>
      )}
    </div>
  );
}
